/**
 * StatusScreen — full-page screen rendered when a route is gated by
 * `maintenance` or `coming_soon`. Uses the page's custom message if
 * provided, otherwise a sensible bilingual default.
 *
 * Anatomy:
 *   • Decorative aurora background (matches the public layout)
 *   • Big icon plate (Wrench / Sparkles)
 *   • Status pill
 *   • Headline + description
 *   • Optional ETA countdown
 *   • CTAs: back home + contact
 *
 * Designed to feel intentional — like a polished launch teaser, not
 * a 404.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  Mail,
  Rocket,
  Wrench,
} from 'lucide-react'
import { Section } from '@/shared/ui/section'
import { Reveal } from '@/shared/motion/reveal'
import { Seo } from '@/shared/seo/seo'
import { Button } from '@/shared/ui/button'
import { defineT, useLocale, useTranslation } from '@/shared/i18n/use-translation'
import { cn } from '@/shared/lib/cn'
import { asset } from '@/shared/lib/asset'
import type { PageStatusKind, PublicPageStatus } from './types'

const T = defineT({
  // Maintenance defaults
  maintTitle: {
    ar: 'الصفحة تحت الصيانة',
    en: 'Page is under maintenance',
  },
  maintDesc: {
    ar: 'بنحدث الصفحة دلوقتي عشان تبقى أحسن. ارجعلها تاني خلال شوية.',
    en: 'We are upgrading this page. Please check back in a few minutes.',
  },
  maintBadge: { ar: 'صيانة', en: 'Maintenance' },
  // Coming soon defaults
  soonTitle: { ar: 'قريباً جداً', en: 'Coming soon' },
  soonDesc: {
    ar: 'بنشتغل على حاجة جميلة. خليك جنبنا.',
    en: 'Something cool is on the way.',
  },
  soonBadge: { ar: 'قريباً', en: 'Coming soon' },
  // Common
  eta: { ar: 'الموعد المتوقع', en: 'Expected back at' },
  countdownDays: { ar: 'يوم', en: 'd' },
  countdownHours: { ar: 'ساعة', en: 'h' },
  countdownMinutes: { ar: 'دقيقة', en: 'm' },
  countdownSeconds: { ar: 'ثانية', en: 's' },
  goHome: { ar: 'العودة للرئيسية', en: 'Back to home' },
  contact: { ar: 'تواصل معنا', en: 'Contact us' },
})

interface StatusScreenProps {
  kind: Extract<PageStatusKind, 'maintenance' | 'coming_soon'>
  page: PublicPageStatus | null
}

export function StatusScreen({ kind, page }: StatusScreenProps) {
  const { t } = useTranslation(T)
  const { locale, isRtl } = useLocale()
  const Arrow = isRtl ? ArrowLeft : ArrowRight

  const isMaintenance = kind === 'maintenance'
  const Icon = isMaintenance ? Wrench : Rocket
  const titleDefault = isMaintenance ? t('maintTitle') : t('soonTitle')
  const descDefault = isMaintenance ? t('maintDesc') : t('soonDesc')
  const badge = isMaintenance ? t('maintBadge') : t('soonBadge')
  // Custom illustrations live in /public — graceful fallback to the
  // glowing icon plate if the file is missing.
  // Note: the looping mp4 versions of these were too large to ship
  // alongside the deploy bundle and timed out on Cloudflare Pages.
  // We've fallen back to the static webp poster only — the icon-plate
  // animations behind still give the screen life.
  const illustration = isMaintenance
    ? '/maintenance-illustration.webp'
    : '/coming-soon-illustration.webp'

  // Custom messages override defaults — pick the locale-appropriate one,
  // falling back to whichever is present.
  const customMessage =
    locale === 'ar'
      ? page?.messageAr || page?.messageEn || ''
      : page?.messageEn || page?.messageAr || ''

  return (
    <>
      <Seo
        title={titleDefault}
        path={page?.path ?? '/maintenance'}
        noIndex
      />
      <Section pad="lg" className="relative isolate overflow-hidden">
        {/* Decorative warm glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10"
        >
          <div
            className={cn(
              'absolute left-1/2 top-1/4 h-[420px] w-[420px] -translate-x-1/2 rounded-full opacity-40 blur-3xl',
              isMaintenance
                ? 'bg-gradient-to-br from-primary/40 via-primary/15 to-transparent'
                : 'bg-gradient-to-br from-accent/40 via-primary/15 to-transparent',
            )}
          />
        </div>

        <div className="relative z-10 mx-auto max-w-xl text-center">
          <Reveal>
            <div className="relative mx-auto h-44 w-44 sm:h-56 sm:w-56">
              {/* Pulsing glow */}
              <span
                aria-hidden
                className="absolute inset-0 animate-pulse-glow rounded-full bg-primary/25 blur-2xl"
              />
              {/* Decorative orbit rings */}
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 rounded-full border border-primary/20"
              />
              <span
                aria-hidden
                className="pointer-events-none absolute inset-3 rounded-full border border-primary/10"
              />
              {/* Image illustration — falls back to the icon plate
                  behind it if the file is missing or fails to load. */}
              <div className="absolute inset-6 overflow-hidden rounded-full border-2 border-primary/30 shadow-[0_0_50px_-10px_hsl(var(--primary)/0.5)]">
                <img
                  src={asset(illustration)}
                  alt=""
                  aria-hidden
                  loading="eager"
                  decoding="async"
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
              </div>
              {/* Icon-plate fallback (sits below the video core) */}
              <span
                aria-hidden
                className="absolute inset-6 -z-10 grid place-items-center rounded-full bg-gradient-to-br from-primary/15 to-primary/8 text-primary ring-1 ring-primary/30"
              >
                <Icon
                  className={cn(
                    'h-12 w-12 sm:h-14 sm:w-14',
                    isMaintenance && 'animate-pulse',
                  )}
                />
              </span>
              {/* Glow accent below */}
              <span
                aria-hidden
                className="pointer-events-none absolute -bottom-2 left-1/2 h-1 w-28 -translate-x-1/2 rounded-full bg-primary/50 blur-sm"
              />
            </div>
          </Reveal>

          <Reveal>
            <span className="mt-6 inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 font-headline text-[10px] uppercase tracking-[0.18em] text-primary">
              {badge}
            </span>
          </Reveal>

          <Reveal>
            <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
              {titleDefault}
            </h1>
          </Reveal>

          <Reveal>
            <p className="mx-auto mt-5 max-w-lg text-base leading-relaxed text-muted-foreground sm:text-lg">
              {customMessage || descDefault}
            </p>
          </Reveal>

          {page?.etaAt && (
            <Reveal>
              <EtaCountdown etaAt={page.etaAt} />
            </Reveal>
          )}

          <Reveal>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link to="/">
                <Button variant="primary" size="md">
                  {t('goHome')}
                  <Arrow className="h-4 w-4" aria-hidden />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" size="md">
                  <Mail className="h-4 w-4" aria-hidden />
                  {t('contact')}
                </Button>
              </Link>
            </div>
          </Reveal>
        </div>
      </Section>
    </>
  )
}

/* --------------------------- countdown --------------------------- */

function EtaCountdown({ etaAt }: { etaAt: string }) {
  const { t } = useTranslation(T)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [])

  const target = new Date(etaAt).getTime()
  if (Number.isNaN(target)) return null

  const diff = Math.max(0, target - now)
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24)
  const minutes = Math.floor((diff / (1000 * 60)) % 60)
  const seconds = Math.floor((diff / 1000) % 60)

  // Don't show a countdown if we're already past the ETA
  if (diff === 0) return null

  return (
    <div className="mt-6 inline-flex flex-col items-center gap-2 rounded-2xl border border-border bg-card/60 px-5 py-4 backdrop-blur-md">
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <Clock className="h-3 w-3" aria-hidden />
        {t('eta')}
      </span>
      <div className="flex items-center gap-2 sm:gap-3" dir="ltr">
        <Cell value={days} label={t('countdownDays')} />
        <Sep />
        <Cell value={hours} label={t('countdownHours')} />
        <Sep />
        <Cell value={minutes} label={t('countdownMinutes')} />
        <Sep />
        <Cell value={seconds} label={t('countdownSeconds')} />
      </div>
    </div>
  )
}

function Cell({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center">
      <span className="font-headline text-3xl font-normal tabular-nums text-foreground sm:text-4xl">
        {value.toString().padStart(2, '0')}
      </span>
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
    </div>
  )
}

function Sep() {
  return (
    <span className="font-headline text-2xl text-muted-foreground/40 sm:text-3xl">
      :
    </span>
  )
}
